export type PixelProvider =
    | "facebook"
    | "google_ads"
    | "tiktok"
    | "twitter"
    | "snap"
    | "linkedin"
    | "pinterest"
    | "raw_html";

export interface PixelDto {
    id: number;
    provider: PixelProvider;
    name: string;
    externalId: string | null;
    config: Record<string, unknown> | null;
    rawHeadHtml: string | null;
    rawBodyHtml: string | null;
    leadEvent: string | null;
    enabled: boolean;
    campaignAttachments: number;
    domainAttachments: number;
    createdAt: string;
    updatedAt: string;
}

export interface CreatePixelRequest {
    provider: PixelProvider;
    name: string;
    externalId?: string;
    config?: Record<string, unknown>;
    rawHeadHtml?: string;
    rawBodyHtml?: string;
    leadEvent?: string;
    enabled?: boolean;
}

export interface UpdatePixelRequest {
    name?: string;
    externalId?: string;
    config?: Record<string, unknown>;
    rawHeadHtml?: string;
    rawBodyHtml?: string;
    leadEvent?: string;
    enabled?: boolean;
}

/** UI metadata per provider — drives the create/edit form's field shape and labels. */
export interface ProviderSpec {
    value: PixelProvider;
    label: string;
    /** field shown for `externalId` (null → no externalId field, e.g. raw_html) */
    externalIdLabel: string | null;
    externalIdPlaceholder?: string;
    /** Extra config fields rendered as inputs and merged into `config` */
    configFields?: { key: string; label: string; placeholder?: string; helpText?: string }[];
    /** Default lead event token */
    defaultLeadEvent: string | null;
    /** Inline help blurb for the form */
    helpText?: string;
}

export const PROVIDER_SPECS: ProviderSpec[] = [
    {
        value: "facebook",
        label: "Facebook (Meta)",
        externalIdLabel: "Pixel ID",
        externalIdPlaceholder: "e.g. 2075924780013736",
        defaultLeadEvent: "Lead",
        helpText:
            "Paste the numeric Pixel ID from Meta Events Manager. The base snippet runs PageView automatically; lead-form submits fire fbq('track','Lead').",
    },
    {
        value: "google_ads",
        label: "Google Ads",
        externalIdLabel: "Conversion ID",
        externalIdPlaceholder: "AW-XXXXXXXXXX",
        configFields: [
            {
                key: "conversionLabel",
                label: "Conversion Label",
                placeholder: "abc123XYZ",
                helpText: "Combined with Conversion ID as send_to: AW-XXXXXX/abc123XYZ on lead submit.",
            },
        ],
        defaultLeadEvent: "conversion",
    },
    {
        value: "tiktok",
        label: "TikTok",
        externalIdLabel: "Pixel ID",
        externalIdPlaceholder: "e.g. C5J1JKR2VWXQ7VG6XXXX",
        defaultLeadEvent: "SubmitForm",
    },
    {
        value: "twitter",
        label: "X (Twitter)",
        externalIdLabel: "Pixel ID",
        externalIdPlaceholder: "e.g. o1234",
        defaultLeadEvent: "tw-lead",
    },
    {
        value: "snap",
        label: "Snapchat",
        externalIdLabel: "Pixel ID",
        externalIdPlaceholder: "UUID",
        defaultLeadEvent: "SIGN_UP",
    },
    {
        value: "linkedin",
        label: "LinkedIn Insight",
        externalIdLabel: "Partner ID",
        externalIdPlaceholder: "e.g. 1234567",
        configFields: [
            {
                key: "conversionId",
                label: "Conversion ID",
                placeholder: "numeric, from Campaign Manager",
                helpText: "Numeric conversion id LinkedIn assigns to the Lead conversion in Campaign Manager.",
            },
        ],
        defaultLeadEvent: "lead",
    },
    {
        value: "pinterest",
        label: "Pinterest",
        externalIdLabel: "Tag ID",
        externalIdPlaceholder: "e.g. 26000000000",
        defaultLeadEvent: "lead",
    },
    {
        value: "raw_html",
        label: "Raw HTML (custom)",
        externalIdLabel: null,
        defaultLeadEvent: null,
        helpText:
            "Paste the snippet exactly as the network gives it. Server-side sanitizer strips event-handler attributes and unknown tags; warnings show below if anything was trimmed. Lead-form events do NOT fire automatically for raw pixels.",
    },
];

export function providerSpec(p: PixelProvider): ProviderSpec | undefined {
    return PROVIDER_SPECS.find((s) => s.value === p);
}
